// src/components/NotificationPrefsPanel.jsx
// 通知の受け取り方（種類ごと）と、不正の疑いの重さ（先生）【2026-11-03】
//   GET/PUT /api/notifications/preferences（サーバー: controllers/notificationPrefsController.js）
//   変えるとすぐ保存する。
import React, { useCallback, useEffect, useState } from 'react';
import Notice, { toast } from './Notice';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });

const MODE_LABEL = [
  ['push', 'スマホにも知らせる'],
  ['app', 'アプリ内だけ'],
  ['off', '受け取らない'],
];
const SEV = {
  critical: { label: '重大',     color: '#b91c1c', bg: '#fee2e2' },
  high:     { label: '要注意',   color: '#c2410c', bg: '#ffedd5' },
  warn:     { label: '注意',     color: '#a16207', bg: '#fef9c3' },
  info:     { label: '参考',     color: '#4b5563', bg: '#f3f4f6' },
  off:      { label: '知らせない', color: '#6b7280', bg: '#f9fafb' },
};
const PUSH_MIN = [
  ['critical', '重大のときだけ'],
  ['high', '要注意以上（今までと同じ）'],
  ['never', 'スマホには送らない（要対応に出るだけ）'],
];

export default function NotificationPrefsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/notifications/preferences', { headers: auth() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '通知の設定を読み込めませんでした');
      setData(j);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setBusy(true);
    try {
      const r = await fetch('/api/notifications/preferences', { method: 'PUT', headers: auth(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '保存できませんでした');
      setData(j);
      toast.success('保存しました');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (err) return <Notice kind="error">{err}</Notice>;
  if (!data) return <p style={{ color: '#999' }}>読み込み中...</p>;

  const fraud = data.fraud;
  const resetAll = () => save({ fraud: { severities: Object.fromEntries(fraud.items.map(it => [it.type, null])) } });

  return (
    <div className="npf">
      {!data.ready && <Notice kind="warn">データベースの更新（v82）がまだなので、ここでは保存できません。通知は今までどおり届きます</Notice>}
      {(!data.push.enabled || data.push.devices === 0) && (
        <Notice kind="info">
          {!data.push.enabled
            ? '上の「アプリのプッシュ通知」がオフなので、スマホには何も届きません（アプリ内には残ります）'
            : 'アプリでログインした端末がまだ無いので、スマホへの通知は届きません。アプリでログインすると届くようになります'}
        </Notice>
      )}

      <h3 className="npf-h">種類ごとの受け取り方</h3>
      <div className="npf-table">
        {data.events.map(ev => (
          <div key={ev.key} className="npf-row">
            <span className="npf-label">{ev.label}</span>
            <span className="npf-seg" role="radiogroup" aria-label={ev.label}>
              {MODE_LABEL.map(([m, l]) => {
                const disabled = busy || !data.ready || (m === 'off' && !ev.allowOff);
                return (
                  <button key={m} type="button" role="radio" aria-checked={ev.mode === m} disabled={disabled}
                          className={'npf-seg-btn' + (ev.mode === m ? ' on' : '')}
                          title={m === 'off' && !ev.allowOff ? 'この知らせは止められません' : undefined}
                          onClick={() => ev.mode !== m && save({ events: { [ev.key]: m } })}>
                    {l}
                  </button>
                );
              })}
            </span>
          </div>
        ))}
      </div>
      <p className="npf-hint">「アプリ内だけ」はアプリ・Web の通知や要対応に残り、スマホには鳴りません。</p>

      {fraud && (
        <>
          <h3 className="npf-h">不正の疑い（打刻の印）</h3>
          <p className="npf-hint" style={{ marginTop: 0 }}>
            印ごとに、どのくらい重く見るかを決められます。重大・要注意はお知らせ（要対応）になり、下の基準でスマホにも知らせます。
            「知らせない」にした印は、お知らせを作らず、表でも参考の扱いになります。重さを変えても、打刻の点数（信頼度）は変わりません。
          </p>
          <div className="npf-pushmin">
            <span className="npf-label">スマホに知らせるのは</span>
            <span className="npf-seg">
              {PUSH_MIN.map(([v, l]) => (
                <button key={v} type="button" disabled={busy || !data.ready}
                        className={'npf-seg-btn' + (fraud.pushMin === v ? ' on' : '')}
                        onClick={() => fraud.pushMin !== v && save({ fraud: { pushMin: v } })}>{l}</button>
              ))}
            </span>
          </div>
          <div className="npf-table">
            {fraud.items.map(it => {
              const cur = it.severity || it.defaultSeverity;
              const sev = SEV[cur] || SEV.info;
              return (
                <div key={it.type} className="npf-row">
                  <span className="npf-label">
                    <span className="npf-sev" style={{ color: sev.color, background: sev.bg }}>{sev.label}</span>
                    {it.label}
                    <small className="npf-desc">{it.description}{it.penalty ? `（減点 ${it.penalty}）` : ''}</small>
                  </span>
                  <select value={it.severity || ''} disabled={busy || !data.ready}
                          onChange={e => save({ fraud: { severities: { [it.type]: e.target.value || null } } })}>
                    <option value="">既定（{(SEV[it.defaultSeverity] || SEV.info).label}）</option>
                    <option value="critical">重大</option>
                    <option value="high">要注意</option>
                    <option value="warn">注意</option>
                    <option value="off">知らせない</option>
                  </select>
                </div>
              );
            })}
          </div>
          <button type="button" className="btn-secondary" style={{ marginTop: '0.6rem', fontSize: '0.85rem' }}
                  disabled={busy || !data.ready || !fraud.items.some(it => it.severity)} onClick={resetAll}>
            印の重さをすべて既定に戻す
          </button>
        </>
      )}
    </div>
  );
}
